import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VehiclesService } from '../../../services/vehicles.service';
import { StatusService } from '../../../services/status.service';
import { VehicleAttachment } from '../../../models/VehicleAttachment';
import {
  VehicleDocumentTag,
  VehicleDocumentType,
  VEHICLE_DOCUMENT_TAG_OPTIONS,
  VEHICLE_DOCUMENT_TYPE_OPTIONS
} from '../../../models/vehicle-document-types';
import { Status } from '../../../models/Status';
import { VEHICLE_BRANDS_OPTIONS } from '../../../shared/constants/vehicle-brands';
import { TableColumn } from '../../../shared/components/table/table.component';
import {
  VehiclePreventiveMaintenanceHistoryService,
  VehiclePreventiveMaintenanceHistorySummary
} from '../../../services/vehicle-preventive-maintenance-history.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';


@Component({
  selector: 'app-vehicle-form',
  templateUrl: './vehicle-form.component.html',
  styleUrls: ['./vehicle-form.component.css']
})
export class VehicleFormComponent implements OnInit {
  form: FormGroup;
  vehicleId?: number;
  isSubmitting = false;
  isViewMode = false;

  pageTitle = 'Novo Veículo';

  availableTabs = [
    { id: 'vehicle-data', label: 'Dados do Veículo', icon: 'fas fa-car', required: true },
    { id: 'attachments', label: 'Anexos', icon: 'fas fa-paperclip', required: false },
    { id: 'preventives', label: 'Preventivas', icon: 'fas fa-tools', required: false }
  ];
  activeTab = 'vehicle-data';
  
  brandOptions = VEHICLE_BRANDS_OPTIONS;
  
  attachments: VehicleAttachment[] = [];
  isLoadingAttachments = false;
  uploading = false;
  uploadProgress = 0;
  isDragOver = false;

  /** Rótulos do upload (enviados ao blob e gravados em tags / attachmentType). */
  documentTypeOptions = VEHICLE_DOCUMENT_TYPE_OPTIONS;
  tagOptions = VEHICLE_DOCUMENT_TAG_OPTIONS;
  selectedDocumentType: VehicleDocumentType = VehicleDocumentType.Identificacao;
  selectedDocumentTag: VehicleDocumentTag = VehicleDocumentTag.CRLV;

  // Histórico de preventivas do veículo
  preventiveSummary: VehiclePreventiveMaintenanceHistorySummary | null = null;
  preventiveHistoryTableData: any[] = [];
  isLoadingPreventives = false;
  preventivesError: string | null = null;

  preventiveHistoryColumns: TableColumn[] = [
    { titulo: 'Preventiva', label: 'preventiveMaintenanceName' },
    {
      titulo: 'Data Execução',
      label: 'executedDate',
      formatter: (value: string | null) => {
        if (!value) return '-';
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString();
      }
    },
    { titulo: 'Km Execução', label: 'executedMileage' },
    { titulo: 'Checklist', label: 'checklistId' },
    { titulo: 'Status', label: 'status' }
  ];

  loadingStatuses = false;
  statusOptions: Status[] = [];

  /** Vínculo com contrato (API) — usado só para exibir situação de locação na visualização. */
  b2BContractId: number | null = null;
  b2CContractId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private vehiclesService: VehiclesService,
    private statusService: StatusService,
    private vehiclePreventiveHistoryService: VehiclePreventiveMaintenanceHistoryService,
    private confirmationService: ConfirmationService
  ) {
    this.form = this.fb.group({
      plateNumber: ['', Validators.required],
      chassisNumber: ['', Validators.required],
      renavam: [''],
      brand: ['', Validators.required],
      model: ['', Validators.required],
      modelYear: [new Date().getFullYear(), [Validators.required, Validators.min(1900)]],
      manufactureYear: [new Date().getFullYear(), [Validators.required, Validators.min(1900)]],
      color: [''],
      fuelType: [''],
      hasBox: [false],
      hasPhoneSupport: [false],
      trackerId: [''],
      ownershipType: ['Próprio', Validators.required],
      status: ['Ativo', Validators.required],
      purchaseDate: [''],
      purchasePrice: [undefined],
      currentMileage: [null],
      currentLocation: [''],
      keyCode: ['', Validators.maxLength(50)],
      notes: [''],
      requiresMaintenance: [false],
      nextMaintenanceDate: ['']
    });
  }

  ngOnInit(): void {
    const urlSegments = this.route.snapshot.url.map(segment => segment.path);
    const lastSegment = urlSegments[urlSegments.length - 1];
    this.isViewMode = lastSegment === 'view';

    this.loadVehicleStatuses();
    const idParam = this.route.snapshot.paramMap.get('id');
    
    if (idParam) {
      this.vehicleId = Number(idParam);
      this.pageTitle = this.isViewMode ? 'Visualizar Veículo' : 'Editar Veículo';
      this.vehiclesService.getById(this.vehicleId).subscribe(v => {
        this.b2BContractId = v.b2BContractId ?? null;
        this.b2CContractId = v.b2CContractId ?? null;
        this.form.patchValue({
          plateNumber: v.plateNumber,
          chassisNumber: v.chassisNumber,
          renavam: v.renavam ?? '',
          brand: v.brand,
          model: v.model,
          modelYear: v.modelYear,
          manufactureYear: v.manufactureYear,
          color: v.color ?? '',
          fuelType: v.fuelType ?? '',
          hasBox: v.hasBox ?? false,
          hasPhoneSupport: v.hasPhoneSupport ?? false,
          trackerId: v.trackerId ?? '',
          ownershipType: v.ownershipType,
          status: this.normalizeStatus(v.status) ?? 'Ativo',
          purchaseDate: v.purchaseDate ? v.purchaseDate.substring(0, 10) : '',
          purchasePrice: v.purchasePrice,
          currentMileage: v.currentMileage,
          currentLocation: v.currentLocation ?? '',
          keyCode: v.keyCode ?? '',
          notes: v.notes ?? '',
          requiresMaintenance: v.requiresMaintenance ?? false,
          nextMaintenanceDate: v.nextMaintenanceDate ? v.nextMaintenanceDate.substring(0, 10) : ''
        });
        if (v.attachments && v.attachments.length > 0) {
          this.attachments = v.attachments;
          this.isLoadingAttachments = false;
        } else {
          this.loadAttachments();
        }
        this.loadPreventiveHistory();
      });
    } else {
      this.pageTitle = 'Novo Veículo';
    }

    if (this.isViewMode) {
      this.form.disable();
    }
  }

  /** Há contrato B2B e/ou B2C — veículo tratado como locado em contrato. */
  get isLinkedToRentalContract(): boolean {
    return this.b2BContractId != null || this.b2CContractId != null;
  }

  getContractRentalSituationLabel(): string {
    return this.isLinkedToRentalContract ? 'Locado (em contrato)' : 'Disponível para locação';
  }

  getVehicleCadastroStatusLabel(): string {
    const s = this.form.get('status')?.value;
    return s != null && String(s).trim() !== '' ? String(s) : '—';
  }

  /** Badge do status cadastral na visualização (Ativo = verde, alinhado à coluna de locação). */
  getVehicleCadastroStatusBadgeClass(): string {
    const raw = (this.form.get('status')?.value ?? '').toString().trim().toLowerCase();
    if (!raw) return 'bg-secondary';
    if (raw === 'ativo' || raw.includes('ativo')) return 'bg-success';
    if (raw === 'inativo' || raw.includes('inativo')) return 'bg-secondary';
    if (raw.includes('pendente')) return 'bg-warning text-dark';
    return 'bg-info text-dark';
  }

  save(): void {
    this.isSubmitting = true;
    const payload = { ...this.form.value } as any;
    
    payload.status = this.normalizeStatus(payload.status) ?? 'Ativo';
    
    if ('renavam' in payload) {
      const renavamValue = payload.renavam;
      if (renavamValue !== undefined && renavamValue !== null && renavamValue !== '') {
        payload.renavamNumber = renavamValue;
      }
      delete payload.renavam;
    }
    
    if (payload.purchaseDate) {
      payload.purchaseDate = this.convertDateToISO(payload.purchaseDate);
    }
    if (payload.nextMaintenanceDate) {
      payload.nextMaintenanceDate = this.convertDateToISO(payload.nextMaintenanceDate);
    }
    
    Object.keys(payload).forEach(key => {
      const value = payload[key];
      if (value === '' || value === null || value === undefined) {
        delete payload[key];
      }
    });
    
    if (this.vehicleId) {
      this.vehiclesService.update(this.vehicleId, payload).subscribe({
        next: () => { 
          this.isSubmitting = false; 
          this.router.navigate(['/fleet/vehicles']); 
        },
        error: () => { 
          this.isSubmitting = false; 
        }
      });
    } else {
      this.vehiclesService.create(payload).subscribe({
        next: () => { 
          this.isSubmitting = false; 
          this.router.navigate(['/fleet/vehicles']); 
        },
        error: () => { 
          this.isSubmitting = false; 
        }
      });
    }
  }

  private convertDateToISO(dateString: string): string {
    if (!dateString) return '';
    if (dateString.includes('T')) return dateString;
    return `${dateString}T00:00:00Z`;
  }

  cancel(): void {
    this.router.navigate(['/fleet/vehicles']);
  }

  loadPreventiveHistory(): void {
    if (!this.vehicleId) {
      return;
    }

    this.isLoadingPreventives = true;
    this.preventivesError = null;

    this.vehiclePreventiveHistoryService.getSummaryByVehicle(this.vehicleId).subscribe({
      next: (summary) => {
        this.preventiveSummary = summary;
        this.preventiveHistoryTableData = summary.history || [];
        this.isLoadingPreventives = false;
      },
      error: () => {
        this.isLoadingPreventives = false;
        this.preventivesError = 'Erro ao carregar histórico de preventivas deste veículo.';
      }
    });
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
  }

  loadVehicleStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Vehicle', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: () => {
          this.loadingStatuses = false;
          this.statusOptions = [
            { id: 1, name: 'Ativo', description: 'Veículo ativo', entityType: 'Vehicle', isActive: true },
            { id: 2, name: 'Inativo', description: 'Veículo inativo', entityType: 'Vehicle', isActive: true }
          ] as Status[];
        }
      });
  }

  /**
   * @param silent — se true, não mostra "Carregando anexos..." (útil após upload para a lista não sumir).
   */
  loadAttachments(opts?: { silent?: boolean }): void {
    if (!this.vehicleId) return;
    const silent = opts?.silent === true;
    if (!silent) {
      this.isLoadingAttachments = true;
    }
    this.vehiclesService.listAttachments(this.vehicleId).subscribe({
      next: list => {
        this.attachments = list || [];
        this.isLoadingAttachments = false;
      },
      error: () => {
        this.isLoadingAttachments = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    if (!this.vehicleId) return;
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (!files.length) return;
    this.uploadFiles(files);
    if (input) input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (!this.vehicleId) return;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (!files.length) return;
    this.uploadFiles(files);
  }

  private uploadFiles(files: File[]): void {
    if (!this.vehicleId || files.length === 0) return;
    this.uploading = true;
    this.uploadProgress = 0;

    const total = files.length;
    let index = 0;

    const uploadNext = () => {
      if (index >= total) {
        this.uploading = false;
        this.uploadProgress = 100;
        // Recarrega em segundo plano para alinhar com o servidor sem esconder a lista
        this.loadAttachments({ silent: true });
        return;
      }

      const file = files[index];
      let progressInterval: any = null;
      this.uploadProgress = Math.round((index / total) * 100);
      
      progressInterval = setInterval(() => {
        if (this.uploadProgress < Math.min(90, Math.round(((index + 0.9) / total) * 100))) {
          this.uploadProgress += 2;
        }
      }, 150);

      const plate = (this.form.get('plateNumber')?.value ?? '').toString().trim();
      this.vehiclesService.uploadAttachment(this.vehicleId!, file, {
        documentType: this.selectedDocumentType,
        tag: this.selectedDocumentTag,
        platePrefix: plate,
        attachmentType: this.selectedDocumentType
      }).subscribe({
        next: created => {
          clearInterval(progressInterval);
          if (created?.id != null) {
            const exists = this.attachments.some(a => a.id === created.id);
            if (!exists) {
              this.attachments = [created, ...this.attachments];
            }
          }
          index++;
          this.uploadProgress = Math.round((index / total) * 100);
          uploadNext();
        },
        error: () => {
          clearInterval(progressInterval);
          index++;
          uploadNext();
        }
      });
    };

    uploadNext();
  }

  removeAttachment(att: VehicleAttachment): void {
    if (!att?.id) return;
    this.confirmationService
      .confirm({
        title: 'Excluir anexo',
        message: `Deseja excluir o documento "${att.fileName}"? Esta ação não pode ser desfeita.`,
        confirmText: 'Sim',
        cancelText: 'Não',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        icon: 'fas fa-trash',
        type: 'danger'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.vehiclesService.removeAttachment(att.id).subscribe({
          next: () => {
            this.attachments = this.attachments.filter(a => a.id !== att.id);
          },
          error: () => {}
        });
      });
  }

  onReplaceAttachmentClick(att: VehicleAttachment, input: HTMLInputElement): void {
    if (!att?.id) return;
    this.confirmationService
      .confirm({
        title: 'Substituir anexo',
        message: `Deseja substituir o documento "${att.fileName}" por outro arquivo?`,
        confirmText: 'Sim',
        cancelText: 'Não',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        icon: 'fas fa-sync-alt',
        type: 'question'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        input.value = '';
        input.click();
      });
  }

  onReplaceFileSelected(att: VehicleAttachment, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !att?.id) return;

    this.uploading = true;
    this.uploadProgress = 30;
    this.vehiclesService.replaceAttachment(att.id, file).subscribe({
      next: updated => {
        this.uploadProgress = 100;
        this.uploading = false;
        const idx = this.attachments.findIndex(a => a.id === att.id);
        if (idx >= 0) {
          this.attachments = [
            ...this.attachments.slice(0, idx),
            updated,
            ...this.attachments.slice(idx + 1)
          ];
        } else {
          this.loadAttachments({ silent: true });
        }
        input.value = '';
      },
      error: () => {
        this.uploading = false;
        this.uploadProgress = 0;
        input.value = '';
      }
    });
  }

  downloadAttachment(att: VehicleAttachment): void {
    if (!att?.fileName && !att?.filePath) return;
    this.vehiclesService.downloadAttachment(att).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.displayAttachmentName(att);
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  private displayAttachmentName(att: VehicleAttachment): string {
    const name = (att.fileName || '').trim();
    if (name && !name.startsWith('file_')) return name;
    if (name.includes('_')) {
      const original = name.split('_').slice(2).join('_');
      if (original) return original;
    }
    return name || 'anexo';
  }

  /** Exibe tipo/tag a partir de attachmentType ou JSON em tags (resposta do blob). */
  attachmentSubtitle(att: VehicleAttachment): string {
    if (att.attachmentType) {
      const byType = this.documentTypeOptions.find(o => o.value === att.attachmentType);
      if (byType) return byType.label;
      return att.attachmentType;
    }
    if (att.tags) {
      try {
        const t = JSON.parse(att.tags) as { documentType?: string; tag?: string; prefix?: string };
        const parts: string[] = [];
        if (t.documentType) {
          const opt = this.documentTypeOptions.find(o => o.value === t.documentType);
          parts.push(opt ? opt.label : t.documentType);
        }
        if (t.tag) {
          const tagOpt = this.tagOptions.find(o => o.value === t.tag);
          parts.push(tagOpt ? tagOpt.label : t.tag);
        }
        if (t.prefix) parts.push(`Placa: ${t.prefix}`);
        if (parts.length) return parts.join(' · ');
      } catch {
        /* ignore */
      }
    }
    return '';
  }

  formatAttachmentSize(size: number | undefined | null): string {
    if (size == null || Number.isNaN(Number(size))) return 'Tamanho não disponível';
    const n = Number(size);
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  private normalizeStatus(input: any): string | undefined {
    if (input === null || input === undefined) return undefined;
    const raw = String(input).trim();
    if (!raw) return undefined;
    
    const noAccents = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const key = noAccents.toLowerCase();
    
    const map: Record<string, string> = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'manutencao': 'Manutenção',
      'vendido': 'Vendido',
      'sinistrado': 'Sinistrado',
      'active': 'Ativo',
      'inactive': 'Inativo',
      'maintenance': 'Manutenção',
      'sold': 'Vendido',
      'totaled': 'Sinistrado',
      'totalled': 'Sinistrado'
    };
    
    return map[key] || undefined;
  }
}




